import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Container, Header, TitleSection, Title, MetaRow, ModuleBadge, MetaKey, DescriptionField,
  DescriptionLabel, DescriptionTextarea, Actions, SaveButton, ResetButton, ConfigBar,
  ConfigGroup, ConfigSelect, ConfigInput, VariableChips, Chip, EditArea, PromptSection,
  SectionLabel, PromptTextarea, ChangeNoteInput, VersionBadge, EmptyState, TooltipAnchor,
  TooltipPortalBubble, WarningBanner, WarningHeader, WarningBody,
} from './PromptEditor.styles';
import { Save, RotateCcw, Info, HelpCircle, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { getModuleLabel } from './promptModuleLabels';

const MODEL_OPTIONS = [
  { value: 'sonnet', label: 'Sonnet (user-facing)' },
  { value: 'haiku', label: 'Haiku (JSON/bulk)' },
  { value: 'opus', label: 'Opus (deep reasoning)' },
  { value: 'map_reader', label: 'Map reader (Fable)' },
];

function Tooltip({ text, children, below = false }) {
  const anchorRef = useRef(null);
  const [pos, setPos] = useState(null);

  const show = useCallback(() => {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    setPos(below
      ? { top: r.bottom + 8, left: r.left + r.width / 2 }
      : { top: r.top - 8, left: r.left + r.width / 2 });
  }, [below]);

  const hide = useCallback(() => setPos(null), []);

  return (
    <TooltipAnchor ref={anchorRef} onMouseEnter={show} onMouseLeave={hide}>
      {children}
      {pos && createPortal(
        <TooltipPortalBubble style={below
          ? { top: pos.top, left: pos.left }
          : { top: pos.top, left: pos.left, transform: 'translateX(-50%) translateY(-100%)' }
        }>
          {text}
        </TooltipPortalBubble>,
        document.body
      )}
    </TooltipAnchor>
  );
}

const TECHNICAL_PROMPT_EXPANDED_KEY = 'promptEditor_technicalPromptExpanded';

function extractVariables(text) {
  if (!text) return [];
  const matches = text.match(/\{\{(\w+)\}\}/g) || [];
  return [...new Set(matches.map(m => m.replace(/[{}]/g, '')))].filter(v => v !== 'if' && v !== '/if');
}

// readOnly: shown, not edited (an instance running the global prompt, #705).
// versioned: false for an instance's own version, which has no history, so
// there is no change note, version name or version badge to show.
const PromptEditor = ({
  template,
  onSave,
  saving,
  onDirtyChange,
  readOnly = false,
  versioned = true,
}) => {
  const [systemPromptTechnical, setSystemPromptTechnical] = useState('');
  const [systemPromptQualitative, setSystemPromptQualitative] = useState('');
  const [userPromptTemplate, setUserPromptTemplate] = useState('');
  const [model, setModel] = useState('sonnet');
  const [maxTokens, setMaxTokens] = useState(1000);
  const [changeNote, setChangeNote] = useState('');
  const [versionName, setVersionName] = useState('');
  const [description, setDescription] = useState('');
  const [technicalExpanded, setTechnicalExpanded] = useState(() => {
    try { return localStorage.getItem(TECHNICAL_PROMPT_EXPANDED_KEY) === '1'; }
    catch { return false; }
  });

  useEffect(() => {
    if (template) {
      setSystemPromptTechnical(template.system_prompt_technical ?? template.system_prompt ?? '');
      setSystemPromptQualitative(template.system_prompt_qualitative || '');
      setUserPromptTemplate(template.user_prompt_template || '');
      setModel(template.model || 'sonnet');
      setMaxTokens(template.max_tokens || 1000);
      setDescription(template.description || '');
      setChangeNote('');
      setVersionName(template.version_name || '');
    }
    // id too: an instance's own version and the global row can share key and version
  }, [template?.id, template?.key, template?.version]);

  const toggleTechnicalExpanded = () => {
    const next = !technicalExpanded;
    setTechnicalExpanded(next);
    try { localStorage.setItem(TECHNICAL_PROMPT_EXPANDED_KEY, next ? '1' : '0'); } catch { /* ignore localStorage failures */ }
  };

  const combinedSystemPrompt = [systemPromptTechnical, systemPromptQualitative]
    .filter(part => String(part || '').trim())
    .join('\n\n');

  const variables = useMemo(
    () => extractVariables((systemPromptTechnical || '') + (systemPromptQualitative || '') + (userPromptTemplate || '')),
    [systemPromptTechnical, systemPromptQualitative, userPromptTemplate]
  );

  // A row can hold a literal model ID or an alias outside the curated list
  // (e.g. gpt-4o for the vision converter) — show it as it is instead of a
  // blank select that would invite an accidental model change on save.
  const modelOptions = MODEL_OPTIONS.some(o => o.value === model)
    ? MODEL_OPTIONS
    : [...MODEL_OPTIONS, { value: model, label: `${model} (mukautettu)` }];

  const hasChanges = template && (
    systemPromptTechnical !== (template.system_prompt_technical ?? template.system_prompt ?? '') ||
    systemPromptQualitative !== (template.system_prompt_qualitative || '') ||
    userPromptTemplate !== (template.user_prompt_template || '') ||
    model !== (template.model || 'sonnet') ||
    maxTokens !== (template.max_tokens || 1000) ||
    description !== (template.description || '')
  );

  // Reported up so the screen can ask before switching prompt (#1099)
  useEffect(() => { onDirtyChange?.(Boolean(hasChanges)); }, [hasChanges, onDirtyChange]);

  const handleSave = () => {
    if (!hasChanges || saving) return;
    onSave({
      systemPrompt: combinedSystemPrompt || null,
      systemPromptTechnical: systemPromptTechnical || '',
      systemPromptQualitative: systemPromptQualitative || '',
      userPromptTemplate,
      model,
      maxTokens: parseInt(maxTokens, 10),
      description,
      changeNote: changeNote || undefined,
      versionName: versionName || undefined,
    });
  };

  const handleReset = () => {
    if (!template) return;
    setSystemPromptTechnical(template.system_prompt_technical ?? template.system_prompt ?? '');
    setSystemPromptQualitative(template.system_prompt_qualitative || '');
    setUserPromptTemplate(template.user_prompt_template || '');
    setModel(template.model || 'sonnet');
    setMaxTokens(template.max_tokens || 1000);
    setDescription(template.description || '');
    setChangeNote('');
    setVersionName('');
  };

  if (!template) {
    return (
      <Container>
        <EmptyState>
          <Info size={32} />
          <div style={{ fontSize: 15 }}>Valitse prompt vasemmalta</div>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <TitleSection>
          <Title>{template.name || template.key}</Title>
          <MetaRow>
            <ModuleBadge>{getModuleLabel(template.category)}</ModuleBadge>
            <MetaKey>{template.key}</MetaKey>
            {versioned && <VersionBadge>v{template.version}</VersionBadge>}
          </MetaRow>
          <DescriptionField>
            <DescriptionLabel>Kuvaus ja käyttötarkoitus</DescriptionLabel>
            <DescriptionTextarea
              readOnly={readOnly}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Moduuli → Milloin (UI/API) → Tuottaa (mitä käyttäjä näkee)"
              rows={4}
            />
          </DescriptionField>
        </TitleSection>
        {!readOnly && (
          <Actions>
            <ResetButton onClick={handleReset} disabled={!hasChanges}>
              <RotateCcw size={14} /> Kumoa
            </ResetButton>
            <SaveButton disabled={!hasChanges || saving} onClick={handleSave}>
              <Save size={14} /> {saving ? 'Tallennetaan...' : 'Tallenna'}
            </SaveButton>
          </Actions>
        )}
      </Header>

      <ConfigBar>
        <ConfigGroup>
          Malli
          <ConfigSelect value={model} onChange={e => setModel(e.target.value)} disabled={readOnly}>
            {modelOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </ConfigSelect>
        </ConfigGroup>
        <ConfigGroup>
          Max tokens
          <ConfigInput
            type="number"
            min="100"
            max="64000"
            step="100"
            readOnly={readOnly}
            value={maxTokens}
            onChange={e => setMaxTokens(parseInt(e.target.value, 10) || 1000)}
          />
        </ConfigGroup>
        {variables.length > 0 && (
          <VariableChips>
            {variables.map(v => <Chip key={v}>{'{{' + v + '}}'}</Chip>)}
          </VariableChips>
        )}
      </ConfigBar>

      <EditArea>
        <PromptSection>
          <SectionLabel>
            Sisältöprompt (domain)
            <Tooltip text="Sisällöllinen ohjaus: rooli, sävy, painotukset ja domain-konteksti. Tarkoitettu asiantuntijoiden muokattavaksi." below>
              <HelpCircle size={13} />
            </Tooltip>
          </SectionLabel>
          <PromptTextarea
            readOnly={readOnly}
            value={systemPromptQualitative}
            onChange={e => setSystemPromptQualitative(e.target.value)}
            placeholder="Rooli, sävy, työpajan konteksti, arviointiperiaatteet..."
            $minHeight="140px"
          />
        </PromptSection>

        <PromptSection>
          <WarningBanner>
            <WarningHeader onClick={toggleTechnicalExpanded}>
              <AlertTriangle size={15} />
              <span>Tekninen prompt (vain kehittäjille)</span>
              {technicalExpanded ? <ChevronDown size={14} style={{ marginLeft: 'auto', opacity: 0.6 }} /> : <ChevronRight size={14} style={{ marginLeft: 'auto', opacity: 0.6 }} />}
            </WarningHeader>
            {technicalExpanded && (
              <WarningBody>
                <ul>
                  <li>Tämä osa sisältää <strong>muotoilusopimukset</strong> ja tekniset reunaehdot (esim. JSON-rakenne, viittausformaatti).</li>
                  <li>Muokkaa vain jos tiedät tarkasti, mitä olet muuttamassa.</li>
                </ul>
                <div style={{ marginTop: 10 }}>
                  <PromptTextarea
                    readOnly={readOnly}
                    value={systemPromptTechnical}
                    onChange={e => setSystemPromptTechnical(e.target.value)}
                    placeholder="JSON-muoto, pisteytysrajat, viittausformaatti, kielen vaatimukset..."
                    $minHeight="180px"
                  />
                </div>
              </WarningBody>
            )}
          </WarningBanner>
        </PromptSection>

        <PromptSection>
          <SectionLabel>
            Tehtäväprompt (user prompt)
            <Tooltip text="Tehtäväkohtainen ohje ja muuttujat. Malli noudattaa tätä kehotetta jokaisessa kutsussa. Turvallista muokata." below>
              <HelpCircle size={13} />
            </Tooltip>
          </SectionLabel>
          <PromptTextarea
            readOnly={readOnly}
            value={userPromptTemplate}
            onChange={e => setUserPromptTemplate(e.target.value)}
            placeholder="Tehtäväkohtaiset ohjeet ja muuttujat..."
            $minHeight="280px"
          />
        </PromptSection>

        {versioned && !readOnly && (<>
        <PromptSection>
          <SectionLabel>Muutoshuomio (valinnainen)</SectionLabel>
          <ChangeNoteInput
            value={changeNote}
            onChange={e => setChangeNote(e.target.value)}
            placeholder="Mitä muutettiin ja miksi..."
          />
        </PromptSection>

        <PromptSection>
          <SectionLabel>
            Nimi tälle versiolle (valinnainen)
            <Tooltip text="Nimetään versio jonka juuri tallennat (uusi nykyinen), ei edellistä. Edellinen nykyinen arkistoidaan historiaan omalla nimellään." below>
              <HelpCircle size={13} />
            </Tooltip>
          </SectionLabel>
          <ChangeNoteInput
            value={versionName}
            onChange={e => setVersionName(e.target.value)}
            placeholder="Esim. PUUKOTETTU 5-kuution prompt"
          />
        </PromptSection>
        </>)}
      </EditArea>
    </Container>
  );
};

export default PromptEditor;
